import { ChevronDown, ChevronUp } from "lucide-react";
import { useState } from "react";

import { EmptyState, Icon, Page, PageHeader, useMinWidth } from "@cntrl/ui";

import { useCapability } from "../../app/bandContext.js";
import styles from "./AssetsScreen.module.scss";
import { useAssetsCrewFallthrough } from "./assetsFallthrough.js";
import { AssetsShareBar } from "./AssetsShareBar.js";
import { BiosCard, LogosCard, PhotosCard } from "./CoreCards.js";
import { EpkCard } from "./EpkCard.js";
import { LinksCard } from "./LinksCard.js";

/**
 * /assets — ONE stacked page, no sub-nav (CD Assets layout): the "Core" section (static header —
 * Logos + Photos 2-up, Link in Bio, Bios, version history) then the collapsible "Press kit / EPK"
 * section with its Hide toggle. Core + EPK ONLY — the production stack is the separate Advance Docs
 * page (/advance). Write-gated like its parts (assets:write); the EPK section additionally gates
 * epk:write inside (a non-AV band_member edits Core but sees the EPK role-denied empty).
 *
 * THE ROSTER MOUNTS THIS PAGE TOO (PO, 2026-09-01), which is also what legacy did: its roster shell
 * embedded the artist screens themselves — *"Embedded artist-screen tabs (Releases/Content/Assets/
 * Calendar/Booking/Marketing) render inside the roster shell"*. The roster tab used to mount
 * `BrandScreen` (the Core cards, no Link in Bio, no EPK); that was an implementation note in the assets
 * audit, never a ruling, and it is what left the two lenses on different Assets pages.
 *
 * All three roster lenses hold `assets:write` AND `epk:write`, so they get this page whole, with no
 * read-only posture anywhere — booking joined them on the same date (migration 788 reseeds its assets
 * axis 'read' → 'full'; `CapabilityEngine.AddEpk` adds its root), under the rule that a roster lens
 * EDITS any page it can see. The gates below are unchanged and still do their real work on the BAND
 * path, where band_member and the production crew genuinely differ.
 */
export interface AssetsScreenProps {
  /** SPA navigation for the phone tile bar's Advance Docs tile. */
  onNavigate?: (route: string) => void;
}

export function AssetsScreen({ onNavigate }: AssetsScreenProps) {
  const canWrite = useCapability("assets:write");
  const canEpk = useCapability("epk:write");
  // The crew fall-through, and it is BAND-PATH only (see the hook): under the roster it returns null so
  // this page can never hand an org seat either the artist-shell `/advance` URL or, worse, the Advance
  // Docs screen rendered under an "Assets" header.
  const crewFallthrough = useAssetsCrewFallthrough();
  const isDesktop = useMinWidth("md");
  // EPK is collapsed by default on the phone (CD mobile: the EPK cards live inside a collapsed
  // disclosure) and open on desktop. `null` = follow the breakpoint; an explicit toggle pins it.
  const [epkPinned, setEpkPinned] = useState<boolean | null>(null);
  const epkOpen = epkPinned ?? isDesktop;

  if (!canWrite) {
    // Crew fall-through (CD): a venue-only seat's /assets bookmark lands on Advance Docs, never an
    // empty page. Seats with no asset surface at all (merch) get the role-denied empty.
    if (crewFallthrough) return crewFallthrough;
    return (
      <Page>
        <EmptyState
          title="Assets aren't available for your role"
          description="Ask a band owner or manager for access to edit logos, photos, and bios."
        />
      </Page>
    );
  }

  return (
    // `Page` drops this title below `lg` (the mobile shell header carries "Assets" beside the logo — ART-187)
    // and keeps it on desktop, so the page passes it unconditionally.
    <Page title="Assets">
      {/* The tile bar LEADS the page on the phone (CD mobile Assets); desktop keeps the EPK share
          card instead, so this renders below `md` only. */}
      {!isDesktop && <AssetsShareBar onNavigate={onNavigate} />}

      <div className={styles.grid}>
        <LogosCard />
        <PhotosCard />
      </div>

      <LinksCard />

      <BiosCard />
      {/* No version-history panel here (CD layout — operator ruling 2026-08-16): snapshots still
          happen server-side on every save; the restore UI can find a home later (Settings?). */}

      {/* Press kit / EPK — collapsible (CD: static Core header, collapsible EPK with a Hide toggle). */}
      <section className={styles.epkSection} aria-labelledby="assets-epk-heading">
        <header className={styles.epkHead}>
          {/* Same bare display-title treatment as the Core header — no eyebrow, at either level
              (PO ruling 2026-08-17, extended to section h2s on the rebuilt pages). */}
          <PageHeader as="h2" id="assets-epk-heading" title="EPK" />
          <button
            type="button"
            className={styles.epkToggle}
            aria-expanded={epkOpen}
            aria-controls="assets-epk-body"
            onClick={() => setEpkPinned(!epkOpen)}
          >
            {epkOpen ? "Hide" : "Show"}
            <Icon icon={epkOpen ? ChevronUp : ChevronDown} size={16} />
          </button>
        </header>
        {epkOpen && (
          <div id="assets-epk-body" className={styles.epkBody}>
            {canEpk ? (
              <EpkCard />
            ) : (
              <EmptyState
                title="EPK isn't available for your role"
                description="The press kit is editable by owners, managers, tour managers and content seats. Ask a band owner for access."
              />
            )}
          </div>
        )}
      </section>
    </Page>
  );
}
